import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import { RestaurantDto } from '../../types';
import './Restaurant.css';

const RestaurantList: React.FC = () => {
  const [restaurants, setRestaurants] = useState<RestaurantDto[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const debounceRef = useRef<NodeJS.Timeout>(undefined);

  // Debounce search
  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchRestaurants = useCallback(async () => {
    setError('');
    try {
      const res = await api.get('/restaurants', { params: { search: debouncedSearch || undefined } });
      setRestaurants(res.data);
    } catch (err) {
      setError('Failed to load restaurants. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/restaurants', {
        name: newName,
        address: newAddress,
        phone: newPhone,
      });
      setShowAdd(false);
      setNewName('');
      setNewAddress('');
      setNewPhone('');
      alert('Restaurant submitted for approval!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add restaurant');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Restaurants</h1>
        <button className="btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
          + Add Restaurant
        </button>
      </div>

      <input
        type="text"
        className="search-input"
        placeholder="Search restaurants..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search restaurants"
      />

      {showAdd && (
        <form className="add-form" onSubmit={handleAdd}>
          <div className="form-group">
            <input placeholder="Restaurant name *" value={newName} onChange={(e) => setNewName(e.target.value)} required />
          </div>
          <div className="form-group">
            <input placeholder="Address" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} />
          </div>
          <div className="form-group">
            <input placeholder="Phone" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
          </div>
          <button type="submit" className="btn-primary btn-sm">Submit for Approval</button>
        </form>
      )}

      {error && <div className="auth-error">{error}</div>}

      {loading ? (
        <p className="loading">Loading...</p>
      ) : restaurants.length === 0 ? (
        <p className="empty">No restaurants found</p>
      ) : (
        <div className="card-grid">
          {restaurants.map((r) => (
            <Link to={`/restaurants/${r.id}/menu`} key={r.id} className="restaurant-card">
              <div className="restaurant-logo">{r.logo ? <img src={r.logo} alt={r.name} /> : r.name[0]}</div>
              <h3>{r.name}</h3>
              {r.address && <p className="restaurant-address">{r.address}</p>}
              {r.phone && <p className="restaurant-phone">{r.phone}</p>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default RestaurantList;
